import { useEffect, useState } from "react";
import { useInspection } from "./InspectionContext";
import type { LocalPhoto } from "./InspectionContext";
import type { AngleOfPhoto, BoundingBox, Damage } from "./types";
import {
  Breadcrumb,
  CloseIcon,
  MoreIcon,
  VehicleContextCard,
} from "./shared";

const ANGLE_LABEL: Record<AngleOfPhoto, string> = {
  FrontOfCar: "Front",
  RearOfCar: "Rear",
  DriverSide: "Driver side",
  PassengerSide: "Passenger side",
  FrontDriverCorner: "Front driver corner",
  FrontPassengerCorner: "Front passenger corner",
  RearDriverCorner: "Rear driver corner",
  RearPassengerCorner: "Rear passenger corner",
  InteriorFront: "Interior · front",
  InteriorRear: "Interior · rear",
  Overhead: "Overhead",
  Underbody: "Underbody",
  Unknown: "Unknown angle",
};

type FlatDamage = {
  photoId: string;
  photoIndex: number;
  angle: AngleOfPhoto;
  damage: Damage;
};

export function SummaryScreen() {
  const { photos, reset } = useInspection();

  const flatDamages: FlatDamage[] = [];
  for (let i = 0; i < photos.length; i++) {
    const p = photos[i]!;
    const a = p.serverResult?.analysis;
    if (!a) continue;
    for (const d of a.Damage) {
      flatDamages.push({
        photoId: p.id,
        photoIndex: i,
        angle: a.AngleOfPhoto,
        damage: d,
      });
    }
  }

  const [previewId, setPreviewId] = useState<string | null>(null);
  const previewPhoto = photos.find((p) => p.id === previewId) ?? null;

  const damagedPhotoCount = photos.filter(
    (p) => (p.serverResult?.analysis.Damage.length ?? 0) > 0,
  ).length;
  const clearPhotoCount = photos.filter(
    (p) => p.serverResult && p.serverResult.analysis.Damage.length === 0,
  ).length;

  const detectedRego = photos
    .map((p) => p.serverResult?.analysis)
    .find((a) => a?.IsRegoVisible)?.RegoNumber;

  const detectedVehicle = photos
    .map((p) => p.serverResult?.analysis.VehicleDescription)
    .find((v): v is string => !!v && v.trim().length > 0);

  return (
    <>
      <Breadcrumb trail={["Inspections", "Open"]} />
      <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Vehicle damage inspection
          </h1>
          <p className="mt-1 text-sm text-text-secondary">
            Review detected damage, add notes, then submit for sign-off.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={reset}
            className="rounded-md border border-sixt-black bg-white px-4 py-2 text-sm font-semibold hover:bg-surface-light"
          >
            Start over
          </button>
        </div>
      </div>

      <VehicleContextCard vehicle={detectedVehicle} rego={detectedRego} />

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
        <section className="space-y-6">
          <InspectionGrid
            photos={photos}
            flatDamages={flatDamages}
            onPreview={setPreviewId}
          />
        </section>
        <aside className="space-y-6">
          <DetectionSummary
            totalDamages={flatDamages.length}
            damagedPhotos={damagedPhotoCount}
            clearPhotos={clearPhotoCount}
            flatDamages={flatDamages}
          />
        </aside>
      </div>

      {previewPhoto && (
        <Lightbox
          photo={previewPhoto}
          damages={flatDamages.filter((d) => d.photoId === previewPhoto.id)}
          onClose={() => setPreviewId(null)}
        />
      )}
    </>
  );
}

/* ─────────────────────────── Grid ─────────────────────────── */

function InspectionGrid({
  photos,
  flatDamages,
  onPreview,
}: {
  photos: LocalPhoto[];
  flatDamages: FlatDamage[];
  onPreview: (id: string) => void;
}) {
  return (
    <div className="rounded-card border border-border-grey bg-white">
      <div className="flex items-center justify-between border-b border-border-grey px-5 py-3">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-bold uppercase tracking-wider">
            Photos
          </h3>
          <span className="rounded-full bg-surface-light px-2 py-0.5 text-xs font-semibold text-text-secondary">
            {photos.length} analysed
          </span>
        </div>
        <div className="flex items-center gap-1 text-xs">
          <FilterChip active>All</FilterChip>
          <FilterChip>Damage</FilterChip>
          <FilterChip>Clear</FilterChip>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 p-5 md:grid-cols-3">
        {photos.map((p, i) => (
          <PhotoTile
            key={p.id}
            photo={p}
            index={i}
            damages={flatDamages.filter((d) => d.photoId === p.id)}
            onPreview={() => onPreview(p.id)}
          />
        ))}
      </div>
    </div>
  );
}

function FilterChip({
  children,
  active,
}: {
  children: React.ReactNode;
  active?: boolean;
}) {
  return (
    <button
      className={
        "rounded-full px-3 py-1 font-semibold transition-colors " +
        (active
          ? "bg-sixt-black text-white"
          : "text-text-secondary hover:bg-surface-light")
      }
    >
      {children}
    </button>
  );
}

function PhotoTile({
  photo,
  index,
  damages,
  onPreview,
}: {
  photo: LocalPhoto;
  index: number;
  damages: FlatDamage[];
  onPreview: () => void;
}) {
  const analysis = photo.serverResult?.analysis;
  const hasDamage = damages.length > 0;
  const noCar = analysis?.VehicleDescription === "No car detected";
  const label = noCar
    ? "No car detected"
    : analysis
    ? ANGLE_LABEL[analysis.AngleOfPhoto]
    : `Photo ${index + 1}`;

  return (
    <div className="group overflow-hidden rounded-card border border-border-grey bg-surface-muted">
      <button
        type="button"
        onClick={onPreview}
        className="relative block aspect-[4/3] w-full overflow-hidden bg-hero-charcoal focus:outline-none focus:ring-2 focus:ring-accent"
        aria-label={`Preview ${label}`}
      >
        <img
          src={photo.previewUrl}
          alt={label}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
        />
        {damages.map((d, i) => (
          <DamageBox key={i} bbox={d.damage.BoundingBox} />
        ))}
      </button>
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          {hasDamage && <DamageAlert />}
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold">{label}</div>
            <div className="text-xs text-text-secondary">
              {!analysis && "Analysis unavailable"}
              {analysis && hasDamage &&
                `${damages.length} damage ${damages.length > 1 ? "points" : "point"}`}
              {analysis && !hasDamage && (noCar ? "No car detected" : "No damage detected")}
            </div>
          </div>
        </div>
        <button className="text-text-secondary hover:text-sixt-black">
          <MoreIcon />
        </button>
      </div>
    </div>
  );
}

function DamageAlert() {
  return (
    <span
      className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#D32F2F] text-[11px] font-black leading-none text-white"
      aria-label="Damage detected"
      title="Damage detected"
    >
      !
    </span>
  );
}

function DamageBox({ bbox }: { bbox: BoundingBox }) {
  const style = {
    left: `${bbox.x1 * 100}%`,
    top: `${bbox.y1 * 100}%`,
    width: `${(bbox.x2 - bbox.x1) * 100}%`,
    height: `${(bbox.y2 - bbox.y1) * 100}%`,
  };
  return (
    <div
      className="absolute rounded-sm border-2 border-accent bg-accent/15 shadow-[0_0_0_1px_rgba(0,0,0,0.25)]"
      style={style}
    />
  );
}

/* ─────────────────────────── Lightbox ─────────────────────────── */

function Lightbox({
  photo,
  damages,
  onClose,
}: {
  photo: LocalPhoto;
  damages: FlatDamage[];
  onClose: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const analysis = photo.serverResult?.analysis;
  const noCar = analysis?.VehicleDescription === "No car detected";
  const label = noCar
    ? "No car detected"
    : analysis
    ? ANGLE_LABEL[analysis.AngleOfPhoto]
    : photo.file.name;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-6"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        aria-label="Close preview"
        className="absolute right-5 top-5 grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
      >
        <CloseIcon />
      </button>

      <div
        className="relative flex max-h-full max-w-6xl flex-col items-center gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative inline-block">
          <img
            src={photo.previewUrl}
            alt={label}
            className="max-h-[80vh] max-w-full rounded-card object-contain shadow-2xl"
          />
          {damages.map((d, i) => (
            <DamageBox key={i} bbox={d.damage.BoundingBox} />
          ))}
        </div>

        <div className="w-full max-w-2xl rounded-card bg-white/95 px-5 py-4 text-sixt-black">
          <div className="flex items-center gap-2">
            {damages.length > 0 && <DamageAlert />}
            <div>
              <div className="text-sm font-bold">{label}</div>
              <div className="text-xs text-text-secondary">
                {damages.length === 0
                  ? noCar
                    ? "No car detected"
                    : "No damage detected"
                  : `${damages.length} damage ${damages.length > 1 ? "points" : "point"}`}
              </div>
            </div>
          </div>
          {damages.length > 0 && (
            <ul className="mt-3 space-y-1.5 text-sm">
              {damages.map((d, i) => (
                <li key={i} className="flex gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  <span>{d.damage.Description}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Sidebar panels ─────────────────────────── */

function DetectionSummary({
  totalDamages,
  damagedPhotos,
  clearPhotos,
  flatDamages,
}: {
  totalDamages: number;
  damagedPhotos: number;
  clearPhotos: number;
  flatDamages: FlatDamage[];
}) {
  return (
    <div className="rounded-card border border-border-grey bg-white p-5 shadow-card">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold uppercase tracking-wider">
          Detection summary
        </h3>
        <span className="rounded-full bg-surface-light px-2 py-0.5 text-[11px] font-semibold text-text-secondary">
          v2.4
        </span>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        <SummaryStat value={String(totalDamages)} label="Damage" accent />
        <SummaryStat value={String(damagedPhotos)} label="Affected" />
        <SummaryStat value={String(clearPhotos)} label="Clear" />
      </div>

      {flatDamages.length > 0 ? (
        <div className="mt-5 space-y-2.5">
          {flatDamages.map((d, i) => (
            <DamageRow
              key={i}
              description={d.damage.Description}
              angle={ANGLE_LABEL[d.angle]}
            />
          ))}
        </div>
      ) : (
        <div className="mt-5 rounded-md border border-border-grey bg-surface-light px-4 py-6 text-center text-sm text-text-secondary">
          No damage detected across any photo.
        </div>
      )}
    </div>
  );
}

function SummaryStat({
  value,
  label,
  accent,
}: {
  value: string;
  label: string;
  accent?: boolean;
}) {
  return (
    <div
      className={
        "rounded-md p-3 text-center " +
        (accent ? "bg-accent text-white" : "bg-surface-light")
      }
    >
      <div className="text-2xl font-black leading-none">{value}</div>
      <div
        className={
          "mt-1 text-[10px] font-semibold uppercase tracking-wider " +
          (accent ? "text-white/90" : "text-text-secondary")
        }
      >
        {label}
      </div>
    </div>
  );
}

function DamageRow({
  description,
  angle,
}: {
  description: string;
  angle: string;
}) {
  return (
    <div className="flex items-start gap-3 rounded-md border border-border-grey px-3 py-2.5">
      <span className="mt-1.5 block h-2 w-2 shrink-0 rounded-sm bg-accent" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold">{description}</div>
        <div className="text-xs text-text-secondary">{angle}</div>
      </div>
    </div>
  );
}

